"use client";

import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  LineChart,
  type LineRow,
} from "@/components/tool-kit/charts/line-chart";
import { ScatterChart } from "@/components/tool-kit/charts/scatter-chart";
import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { CapmCalculatorStrings } from "@/components/tools/capm-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type BetaRegression,
  capm,
  smlPoints,
} from "@/lib/tool-kit/accounting/capm";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { linearFit } from "@/lib/tool-kit/charts/regression";
import { boundedRange } from "@/lib/tool-kit/charts/scale";
import { CHART_INK } from "@/lib/tool-kit/palette";

type Strings = CapmCalculatorStrings;

const SML_STEPS = 8;

function chartStyle(title: string, xTitle: string, yTitle: string) {
  return {
    title,
    xTitle,
    yTitle,
    color: "blue" as const,
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  };
}

function SmlChart({
  rf,
  rm,
  beta,
  strings,
}: {
  rf: number;
  rm: number;
  beta: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const top = Math.max(2, Math.ceil(beta * 4) / 4);
  const bottom = Math.min(0, Math.floor(beta * 4) / 4);
  const points = smlPoints(rf, rm, bottom, top, SML_STEPS, [beta]);
  const names = [strings.seriesLine, strings.seriesAsset];
  const rows: LineRow[] = points.map((p) => ({
    label: fmt(p.beta, { maximumFractionDigits: 3 }),
    x: p.beta,
    values: [
      p.expectedReturn * 100,
      p.beta === beta ? p.expectedReturn * 100 : null,
    ],
  }));
  return (
    <ChartFigure
      apa={false}
      data={toTsv([
        [strings.smlX, ...names],
        ...rows.map((r) => [r.label, ...r.values]),
      ])}
      figureNumber="1"
      filename={chartFilename(strings.smlTitle, "security-market-line")}
      labels={strings}
      note=""
      table={null}
      title={strings.smlTitle}
    >
      <LineChart
        description={t(strings.smlDescription, {
          rf: fmt(rf * 100, { maximumFractionDigits: 2 }),
          rm: fmt(rm * 100, { maximumFractionDigits: 2 }),
          beta: fmt(beta, { maximumFractionDigits: 3 }),
        })}
        fmt={fmt}
        id="capm-sml"
        label={strings.smlTitle}
        markers
        names={names}
        numeric
        rows={rows}
        style={chartStyle(strings.smlTitle, strings.smlX, strings.smlY)}
        zero={false}
      />
    </ChartFigure>
  );
}

export function RegressionBlock({
  fit,
  asset,
  market,
  rfPeriod,
  strings,
}: {
  fit: BetaRegression;
  asset: number[];
  market: number[];
  rfPeriod: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const num = (v: number, max = 4) => fmt(v, { maximumFractionDigits: max });
  const xs = market.map((v) => v - rfPeriod);
  const ys = asset.map((v) => v - rfPeriod);
  const line = linearFit(xs, ys);
  const title = strings.scatterTitle;
  return (
    <div className="space-y-4">
      <h3 className={WIDGET_H3}>{strings.betaHeading}</h3>
      <StatGrid>
        <StatCard emphasis label={strings.beta} value={num(fit.beta)} />
        <StatCard label={strings.alpha} value={`${num(fit.alpha)}%`} />
        <StatCard label={strings.rSquared} value={num(fit.rSquared)} />
        <StatCard label={strings.correlation} value={num(fit.correlation)} />
        <StatCard
          label={strings.standardError}
          value={fit.standardError === null ? "—" : num(fit.standardError)}
        />
        <StatCard
          label={strings.interval}
          value={
            fit.interval
              ? `${num(fit.interval[0])} – ${num(fit.interval[1])}`
              : "—"
          }
        />
        <StatCard label={strings.adjusted} value={num(fit.adjustedBeta)} />
        <StatCard label={strings.observations} value={fmt(fit.n)} />
      </StatGrid>
      <p className="font-mono text-muted-foreground text-sm leading-relaxed">
        {t(strings.betaWorking, {
          cov: num(fit.covariance, 6),
          variance: num(fit.varianceMarket, 6),
          beta: num(fit.beta),
        })}
      </p>
      {fit.n < 24 ? <Notice>{strings.fewObservations}</Notice> : null}
      {line ? (
        <ChartFigure
          apa={false}
          data={toTsv([
            [strings.scatterX, strings.scatterY],
            ...xs.map((x, i) => [x, ys[i]]),
          ])}
          figureNumber="2"
          filename={chartFilename(title, "beta-regression")}
          labels={strings}
          note=""
          table={null}
          title={title}
        >
          <ScatterChart
            description={t(strings.scatterDescription, {
              n: fmt(fit.n),
              beta: num(fit.beta, 3),
            })}
            fits={[{ name: title, color: CHART_INK.text, fit: line }]}
            fmt={fmt}
            id="capm-scatter"
            label={title}
            names={[title]}
            points={xs.map((x, i) => ({ x, y: ys[i], group: 0, key: i }))}
            showEquation
            style={chartStyle(title, strings.scatterX, strings.scatterY)}
            xRange={boundedRange(xs, null, null)}
            yRange={boundedRange(ys, null, null)}
          />
        </ChartFigure>
      ) : null}
    </div>
  );
}

export function CapmBlock({
  rf,
  rm,
  beta,
  premiumGiven,
  strings,
}: {
  rf: number;
  rm: number;
  beta: number;
  premiumGiven: boolean;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const result = capm(rf, beta, rm);
  const pct = (v: number) => `${fmt(v * 100, { maximumFractionDigits: 4 })}%`;
  const num = (v: number) => fmt(v, { maximumFractionDigits: 4 });
  const values = {
    rf: pct(rf),
    rm: pct(rm),
    beta: num(beta),
    premium: pct(result.marketPremium),
    result: pct(result.expectedReturn),
  };
  const table = [0, 0.5, 1, 1.5, 2].map((b) => [
    num(b),
    pct(capm(rf, b, rm).expectedReturn),
  ]);
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.expected}
          value={pct(result.expectedReturn)}
        />
        <StatCard
          label={strings.marketPremium}
          value={pct(result.marketPremium)}
        />
        <StatCard
          label={strings.assetPremium}
          value={pct(result.assetPremium)}
        />
        <StatCard label={strings.betaUsed} value={num(beta)} />
      </StatGrid>
      <div className={`${CARD} space-y-1`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {t(premiumGiven ? strings.workPremium : strings.workReturn, values)}
        </p>
      </div>
      <SmlChart beta={beta} rf={rf} rm={rm} strings={strings} />
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[strings.betaColumn, strings.requiredColumn]}
        rows={table}
      />
    </section>
  );
}
